import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { getCommonPinningStyles } from '@repo/lib';
import { flexRender, Header } from '@tanstack/react-table';
import { DataTableResizer, TableHead } from '@ui/components';
import { cn } from '@ui/lib/utils';
import { CSSProperties, HTMLProps, useMemo } from 'react';

interface DataTableDragHeaderProps<TData, TValue> {
  classNameHeader?: HTMLProps<HTMLElement>['className'];
  header: Header<TData, TValue>;
  isDraggable?: boolean;
}

export const DataTableDragHeader = <TData, TValue>(
  props: DataTableDragHeaderProps<TData, TValue>,
) => {
  const { header, classNameHeader, isDraggable } = props;

  const _isDraggable = useMemo(
    () =>
      header.column.id !== 'select' &&
      header.column.id !== 'actions' &&
      isDraggable,
    [header.column.id, isDraggable],
  );

  const { attributes, isDragging, listeners, setNodeRef, transform } =
    useSortable({
      id: header.column.id,
      disabled: !_isDraggable,
    });

  const style: CSSProperties = {
    transform: CSS.Translate.toString(transform), // translate instead of transform to avoid squishing
    transition: 'width transform 0.2s ease-in-out',
    whiteSpace: 'nowrap',
  };

  return (
    <TableHead
      key={header.id}
      ref={setNodeRef}
      className={cn(
        'group relative text-xs font-semibold [&:not(:last-child)]:border-r',
        _isDraggable && 'cursor-move',
        classNameHeader,
      )}
      colSpan={header.colSpan}
      style={{
        ...getCommonPinningStyles({ column: header.column, isDragging }),
        ...style,
        width: `calc(var(--header-${header.id}-size) * 1px)`,
      }}
      {...attributes}
      {...listeners}
    >
      <div className="flex size-full items-center justify-center">
        {header.isPlaceholder
          ? null
          : flexRender(header.column.columnDef.header, header.getContext())}
      </div>

      <DataTableResizer header={header} />
    </TableHead>
  );
};

interface DataTableDragHeaderFilterProps<TData, TValue> {
  header: Header<TData, TValue>;
  isDraggable?: boolean;
}

export const DataTableDragHeaderFilterProps = <TData, TValue>(
  props: DataTableDragHeaderFilterProps<TData, TValue>,
) => {
  const { header, isDraggable } = props;
  const isCanFilter = header.column.getCanFilter();

  const { setNodeRef, transform } = useSortable({
    id: header.column.id,
    disabled:
      header.column.id === 'select' ||
      header.column.id === 'actions' ||
      !isDraggable,
  });

  const style: CSSProperties = {
    transform: CSS.Translate.toString(transform), // translate instead of transform to avoid squishing
    transition: 'width transform 0.2s ease-in-out',
    whiteSpace: 'nowrap',
  };

  return (
    <TableHead
      key={header.id}
      ref={setNodeRef}
      className="h-7 w-full p-0 [&:not(:last-child)]:border-r"
      style={{
        ...getCommonPinningStyles({
          column: header.column,
        }),
        width: `calc(var(--col-${header.column.id}-size) * 1px)`,
        ...style,
      }}
    >
      {isCanFilter && (
        <input
          className="size-full px-2 outline-0"
          placeholder="Filter..."
          value={String(header.column?.getFilterValue() ?? '')}
          onChange={(event) =>
            header.column?.setFilterValue(event.target.value)
          }
        />
      )}
    </TableHead>
  );
};
